import { useRef, useEffect } from "react";
import type { VadState } from "@/voice/types";

interface WaveViewCanvasProps {
  width: number;
  height: number;
  active?: boolean;
  /** 原始音频是否静音（振幅过低，不显示波形） */
  audioSilent?: boolean;
  statusText?: string;
  vadState?: VadState;
  lineWidth?: number;
  /** 获取真实波形数据的方法 */
  getWaveData?: () => Uint8Array;
  /** TTS 正在播放中，显示模拟波纹 */
  isTtsPlaying?: boolean;
}

/**
 * 纯 Canvas 波形组件
 *
 * audioSilent=true → 不显示波形（静音/底噪）
 * 有 getWaveData → 绘制真实音频波形
 * 无 getWaveData → 绘制模拟波形（fallback）
 */
export default function WaveViewCanvas({
  width,
  height,
  active = false,
  audioSilent = false,
  statusText,
  vadState,
  lineWidth,
  getWaveData,
  isTtsPlaying = false,
}: WaveViewCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    const midY = height / 2;
    const lw = lineWidth ?? 2;
    const hasRealData = !!getWaveData;

    function draw() {
      ctx.clearRect(0, 0, width, height);

      // 有真实音频数据且非静音 → 用真实数据绘制
      if (active && !audioSilent && hasRealData) {
        const data = getWaveData!();
        if (data.length > 0) {
          drawRealWaveform(ctx, data, width, height, midY, lw);
        } else {
          drawFlatLine(ctx, width, midY, lw);
        }
      } else if (isTtsPlaying) {
        // ★ TTS 播放中 → 显示模拟心跳波纹（不依赖麦克风数据）
        drawSimulatedWave(ctx, width, height, midY, lw, "speaking");
      } else if (!active || audioSilent) {
        drawFlatLine(ctx, width, midY, lw);
      } else {
        drawSimulatedWave(ctx, width, height, midY, lw, vadState);
      }

      animRef.current = requestAnimationFrame(draw);
    }

    draw();
    return () => cancelAnimationFrame(animRef.current);
  }, [width, height, lineWidth, active, audioSilent, vadState, getWaveData, isTtsPlaying]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
      }}
    >
      {active ? (
        <canvas
          ref={canvasRef}
          style={{
            width,
            height,
            minHeight: height + 12,
            borderRadius: 20,
            background: "#F0FDF4",
            border: "1px solid #BBF7D0",
          }}
        />
      ) : (
        <div
          style={{
            width,
            height: Math.max(height, 36),
            borderRadius: 20,
            border: "1px solid #E5E7EB",
            background: "#FAFAFA",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span
            className="text-xs"
            style={{
              color: "#bbb",
              letterSpacing: "0.02em",
              padding: "0 8px",
              textAlign: "center",
            }}
          >
            ──
          </span>
        </div>
      )}

      {statusText && (
        <p
          className="text-xs text-center mt-1.5"
          style={{
            color:
              vadState === "speaking"
                ? "#00C851"
                : active
                  ? "#666"
                  : "#bbb",
            transition: "color 0.3s ease",
          }}
        >
          {statusText}
        </p>
      )}
    </div>
  );
}

/** 绘制平直线 */
function drawFlatLine(
  ctx: CanvasRenderingContext2D,
  w: number,
  midY: number,
  lw: number,
) {
  ctx.beginPath();
  ctx.moveTo(0, midY);
  ctx.lineTo(w, midY);
  ctx.strokeStyle = "rgba(0,200,81,0.15)";
  ctx.lineWidth = lw;
  ctx.stroke();
}

/** 绘制真实音频数据（心电图/示波器风格） */
function drawRealWaveform(
  ctx: CanvasRenderingContext2D,
  data: Uint8Array,
  w: number,
  h: number,
  midY: number,
  lw: number,
) {
  const len = data.length;
  const step = w / len;

  // 计算整体强度
  let maxDev = 0;
  let rmsSum = 0;
  for (let i = 0; i < len; i++) {
    const d = Math.abs(data[i] - 128);
    if (d > maxDev) maxDev = d;
    rmsSum += d * d;
  }
  const intensity = Math.min(maxDev / 50, 1);
  if (intensity < 0.02) {
    drawFlatLine(ctx, w, midY, lw);
    return;
  }

  // 背景光晕
  const bgGrad = ctx.createLinearGradient(
    0,
    midY - h * 0.35,
    0,
    midY + h * 0.35,
  );
  bgGrad.addColorStop(0, `rgba(0,200,81,${0.06 * intensity})`);
  bgGrad.addColorStop(0.5, `rgba(0,180,73,${0.03 * intensity})`);
  bgGrad.addColorStop(1, `rgba(0,200,81,${0.06 * intensity})`);
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, w, h);

  // 心电图/示波器风格：非线性压缩，低振幅贴近基线，高振幅尖锐突出
  const maxAmp = h * 0.4;

  // 主线
  ctx.beginPath();
  for (let i = 0; i < len; i++) {
    const rawV = (data[i] - 128) / 128; // -1 ~ 1

    // 非线性压缩：小值压得更小（贴近基线），大值保留（尖锐峰）
    // sign(v) * |v|^1.5 的效果：0.1→0.03, 0.5→0.35, 1.0→1.0
    const sign = rawV >= 0 ? 1 : -1;
    const compressedV = sign * Math.pow(Math.abs(rawV), 1.6);

    const x = i * step;
    const y = midY + compressedV * maxAmp;

    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.strokeStyle = `rgba(0,200,81,${0.7 * intensity + 0.2})`;
  ctx.lineWidth = lw * 1.3;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.stroke();

  // 辅助线（镜像、稍暗、略细）
  ctx.beginPath();
  for (let i = 0; i < len; i++) {
    const rawV = (data[i] - 128) / 128;
    const sign = rawV >= 0 ? 1 : -1;
    const compressedV = sign * Math.pow(Math.abs(rawV), 1.6);

    const x = i * step;
    const y = midY + compressedV * maxAmp * 0.55;

    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.strokeStyle = `rgba(0,180,73,${0.25 * intensity + 0.05})`;
  ctx.lineWidth = lw * 0.65;
  ctx.stroke();

  // 基线参考线（微弱的水平中线）
  ctx.beginPath();
  ctx.moveTo(0, midY);
  ctx.lineTo(w, midY);
  ctx.strokeStyle = `rgba(0,200,81,${0.08 * intensity})`;
  ctx.lineWidth = 0.5;
  ctx.stroke();
}

let simFrame = 0;

/** 绘制模拟波形（心跳脉冲风格） */
function drawSimulatedWave(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  midY: number,
  lw: number,
  vadState?: VadState,
) {
  simFrame++;

  let amp = 0;
  if (vadState === "speaking") amp = 0.7 + 0.3 * Math.sin(simFrame * 0.03);
  else if (vadState === "silence_detecting") amp = 0.35;
  else if (vadState === "recording")
    amp = 0.18 + 0.1 * Math.sin(simFrame * 0.02);

  if (amp < 0.01) {
    drawFlatLine(ctx, w, midY, lw);
    return;
  }

  // 心跳脉冲参数
  const t = simFrame * 0.04; // 时间偏移（控制脉冲从左向右流动）
  const pulseCount = 4; // 同时显示的脉冲数
  const baseWidth = w / pulseCount; // 每个脉冲占据的空间宽度

  // 背景光晕
  const bgGrad = ctx.createLinearGradient(
    0,
    midY - h * 0.35,
    0,
    midY + h * 0.35,
  );
  bgGrad.addColorStop(0, `rgba(0,200,81,${0.06 * amp})`);
  bgGrad.addColorStop(0.5, `rgba(0,180,73,${0.03 * amp})`);
  bgGrad.addColorStop(1, `rgba(0,200,81,${0.06 * amp})`);
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, w, h);

  // 绘制心跳脉冲波形
  const maxAmp = h * 0.38 * amp;

  // 主线（较亮）
  ctx.beginPath();
  for (let x = 0; x <= w; x += 1) {
    const nx = x / w;
    // 计算当前点落在哪个脉冲周期内
    const phase = (nx * pulseCount + t * 0.8) % 1; // 0~1 循环
    let y = midY;

    if (phase < 0.05) {
      // 小前波（P 波）
      const p = phase / 0.05;
      y = midY - Math.sin(p * Math.PI) * maxAmp * 0.2;
    } else if (phase < 0.15) {
      // 回到基线
      y = midY;
    } else if (phase < 0.22) {
      // 主尖峰上升沿（R 波）
      const p = (phase - 0.15) / 0.07;
      y = midY - Math.sin(p * Math.PI) * maxAmp;
    } else if (phase < 0.32) {
      // 主尖峰下降沿过冲
      const p = (phase - 0.22) / 0.1;
      y = midY + Math.sin(p * Math.PI) * maxAmp * 0.25;
    } else if (phase < 0.45) {
      // 回到基线
      const p = (phase - 0.32) / 0.13;
      y = midY - Math.sin(p * Math.PI) * maxAmp * 0.15 * (1 - p);
    }
    // 其余时间保持基线

    if (x === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.strokeStyle = `rgba(0,200,81,${0.7 * amp + 0.2})`;
  ctx.lineWidth = lw * 1.1;
  ctx.stroke();

  // 辅助线（稍暗、振幅略小）
  ctx.beginPath();
  for (let x = 0; x <= w; x += 1) {
    const nx = x / w;
    const phase = (nx * pulseCount + t * 0.8) % 1;
    let y = midY;

    if (phase < 0.05) {
      const p = phase / 0.05;
      y = midY - Math.sin(p * Math.PI) * maxAmp * 0.14;
    } else if (phase < 0.15) {
      y = midY;
    } else if (phase < 0.22) {
      const p = (phase - 0.15) / 0.07;
      y = midY - Math.sin(p * Math.PI) * maxAmp * 0.7;
    } else if (phase < 0.32) {
      const p = (phase - 0.22) / 0.1;
      y = midY + Math.sin(p * Math.PI) * maxAmp * 0.18;
    } else if (phase < 0.45) {
      const p = (phase - 0.32) / 0.13;
      y = midY - Math.sin(p * Math.PI) * maxAmp * 0.1 * (1 - p);
    }

    if (x === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.strokeStyle = `rgba(0,180,73,${0.3 * amp + 0.06})`;
  ctx.lineWidth = lw * 0.65;
  ctx.stroke();
}
